<h4 class="title-2 mb-4" data-test-id="authorization-details">Authorization Details</h4>

<form [formGroup]="authorizationForm">
  <p-panel [toggleable]="false"
    header="Please enter the details for the authorization request" data-test-id="authorization-details-header">
    @if (isLoading) {
      <p-progressSpinner />
    }

    @if (!isLoading) {
      <div class="grid grid-cols-12 gap-4 p-fluid p-form-widget">
        <div class="col-span-12 lg:col-span-6">
          <div class="field">
            <label data-test-id="order-type"><span class="is-required">*</span>Type of Order:</label>
            <div class="p-field-value">
              <p-select [options]="orderTypes"
                formControlName="orderType"
                placeholder="Select"
                [readonly]="isExternalUser"
              data-test-id="order-type-dropdown"></p-select>
            </div>
          </div>
          <div class="field">
            <label data-test-id="order-sub-type"><span class="is-required">*</span> Subtype of Order: </label>
            <div class="p-field-value">
              <p-select [filter]="true"
                [options]="subOrderTypes"
                formControlName="subType"
                placeholder="Select"
                data-test-id="order-sub-type-dropdown">
              </p-select>
            </div>
          </div>
          <div class="field">
            <label data-test-id="urgency"><span class="is-required">*</span> Urgency: </label>
            <div class="p-field-value">
              <p-select [options]="urgencies"
                formControlName="urgency"
                placeholder="Select"
                (onChange)="handleUrgencyChange($event.value)"
                data-test-id="urgency-dropdown">
              </p-select>
            </div>
          </div>
          <div class="field">
            <label data-test-id="referral-source"><span class="is-required">*</span> Referral Source: </label>
            <div class="p-field-value">
              <p-select [options]="referralSources"
                formControlName="referalSource"
                placeholder="Select"
                data-test-id="referral-source-dropdown">
              </p-select>
            </div>
          </div>
          <div class="field">
            <label data-test-id="referring-facility">@if (referralRequired) {
              <span class="is-required">*</span>
            }Referring Facility: </label>
            <div class="p-field-value">
              <input type="text"
                pInputText
                formControlName="referralName"
                data-test-id="referring-facility-value"/>
              </div>
            </div>
            @if (authorizationForm.get('referralName').value) {
              <div class="field">
                <label data-test-id="referring-facility-npi">Referring Facility NPI: </label>
                <div class="p-field-value">
                  <input type="text"
                    pInputText
                    formControlName="referringFacilityNPI"
                    data-test-id="referring-facility-npi-value" />
                  </div>
                </div>
              }
            </div>
            <div class="col-span-12 lg:col-span-6">
              <div class="field">
                <label data-test-id="facility-discharge-date">@if (referralRequired) {
                  <span class="is-required">*</span>
                }Facility Discharge Date:</label>
                <div class="p-field-value">
                  <p-date-picker formControlName="facilityDischargeDate"
                    [showTime]="false"
                    [showIcon]="true"
                    appendTo="body"
                    [minDate]="minFacilityDischargeDate"
                    [readonlyInput]="true"
                    data-test-id="facility-discharge-date-calendar"/>
                    @if (authorizationForm.controls['facilityDischargeDate'].hasError('minDate')
                      && authorizationForm.touched && !authorizationForm.controls['facilityDischargeDate'].hasError('required')) {
                      <small class="p-error">
                        Facility Discharge Date has exceeded more than 180 days from when the request was received. Please enter the most recent facility discharge date.
                      </small>
                    }
                  </div>
                </div>
                <div class="field">
                  <label data-test-id="requestor"><span class="is-required">*</span>Requestor:</label>
                  <div class="p-field-value">
                    <p-select [options]="requestors"
                      formControlName="requestorName"
                      placeholder="Select"
                      data-test-id="requestor-dropdown">
                    </p-select>
                  </div>
                </div>
                <ng-container *accessView="['ServiceLine.RequestSource.Read']">
                  @if (!isExternalUser) {
                    <div class="field">
                      <label data-test-id="request-source"><span class="is-required">*</span>Request Source:</label>
                      <div class="p-field-value">
                        <p-select [options]="requestSources"
                          formControlName="requestSource"
                          placeholder="Select"
                          data-test-id="request-source-dropdown">
                        </p-select>
                      </div>
                    </div>
                  }
                </ng-container>
                @if (!isExternalUser) {
                  <div class="field">
                    <label data-test-id="init-request-date">
                      @if (!setDateTimeFaxReceivedReadonly) {
                        <span class="is-required">*</span>
                      }
                      Initial Request Received Date:
                    </label>
                    <div class="p-field-value flex">
                      @if (!setDateTimeFaxReceivedReadonly) {
                        <div class="p-field-value">
                          <p-date-picker appendTo="body"
                            formControlName="dateTimeFaxReceivedDate"
                            [readonlyInput]="true"
                            [showTime]="false"
                            [showIcon]="true"
                            [maxDate]="maxDate"
                          data-test-id="init-request-date-calendar"></p-date-picker>
                        </div>
                      } @else {
                        <div class="p-2">
                          {{ this.authorizationForm.value.dateTimeFaxReceivedDate | date : 'MM/dd/yyyy'}}
                        </div>
                      }
                      <label data-test-id="init-request-time" class="font-semibold p-2">Time:</label>
                      @if (!setDateTimeFaxReceivedReadonly) {
                        <div class="p-field-value">
                          <p-date-picker appendTo="body"
                            hourFormat="12"
                            formControlName="dateTimeFaxReceivedTime"
                            [readonlyInput]="true"
                            [showTime]="true"
                            [showSeconds]="true"
                            [showIcon]="true"
                            [timeOnly]="true"
                          data-test-id="init-request-time-calendar"></p-date-picker>
                        </div>
                      } @else {
                        <div class="p-2">
                          {{ this.authorizationForm.value.dateTimeFaxReceivedTime | date : 'hh:mm:ss a'}}
                        </div>
                      }
                    </div>
                  </div>
                }
                <div class="field">
                  <label data-test-id="care-start-date"><span class="is-required">*</span>Start of Care Date: </label>
                  @if (!socConfirmed) {
                    <div class="p-field-value">
                      <p-date-picker appendTo="body"
                        formControlName="startOfCareDate"
                        [readonlyInput]="true"
                        [showTime]="false"
                        [showIcon]="true"
                        [maxDate]="maxSocDate"
                        (onSelect)="handleStartOfCareDateChanges($event)"
                      data-test-id="care-start-date-calendar"></p-date-picker>
                      @if (isSocLoading) {
                        <p-progressSpinner></p-progressSpinner>
                      }
                      @if (!socConfirmed && authorizationForm.controls['startOfCareDate'].hasError('required') && authorizationForm.touched) {
                        <small class="p-error">
                          Start of Care Date is required.
                        </small>
                      }
                    </div>
                  }
                  @if (socConfirmed) {
                    <div class="p-field-value p-disabled">
                      <div class="readonly-field" data-test-id="care-start-date-value">
                        {{authRequest.authorizationDTO.startOfCareDate | date: 'MM/dd/yyyy'}}
                      </div>
                    </div>
                  }
                </div>
                <div class="field">
                  <label data-test-id="cert-period">Cert Period:</label>
                  <div class="p-field-value">
                    <input type="text"
                      pInputText
                      formControlName="certPeriod"
                      data-test-id="cert-period-value" />
                    </div>
                  </div>
                </div>
              </div>
            }
          </p-panel>
        </form>

        <br />
        <!--Immediate Contact Details-->
        <app-auth-request-immediate-contact [authRequest]="authRequest"
          [isExternalUser]="isExternalUser"
          (immediateContactDetailsFormValid)="immediateContactDetailsFormValid($event)"
          (authRequestUpdated)="authRequestUpdated($event)">
        </app-auth-request-immediate-contact>

        <!-- Acknowledgement Dialog -->
        <p-confirmDialog [transitionOptions]="'200ms'"
          [rejectVisible]="false"
          [acceptLabel]="'Continue'"
          appendTo="body"
          styleClass="half-width-view-dialog">
        </p-confirmDialog>

        @if (displaySocDialog) {
          <p-dialog
            appendTo="body"
            header="Please confirm that Start of Care date is"
            [draggable]="false"
            [modal]="true"
            [closable]="false"
            [minY]="70"
            [maximizable]="false"
            [(visible)]="displaySocDialog"
            data-test-id="care-start-date-dialog">
            <div class="soc-confirmation-dialog">
              <form [formGroup]="socConfirmationForm">
                <div class="formgroup-inline">
                  <div class="field">
                    <label class="label" data-test-id="care-start-date-dialog-label">Start of Care Date:</label>
                    <p-date-picker appendTo="body"
                      formControlName="startOfCareDate"
                      [readonlyInput]="true"
                      [showTime]="false"
                      [showIcon]="true"
                      [showOnFocus]="false"
                      [maxDate]="maxSocDate"
                      (onSelect)="handleStartOfCareDateChanges($event)"
                      data-test-id="care-start-date-dialog-calendar">
                    </p-date-picker>
                  </div>
                </div>
              </form>
            </div>
            <ng-template #footer>
              <button type="button"
                pButton
                label="Confirm"
                (click)="handleStartOfCareDateConfirmation()"
                data-test-id="care-start-date-dialog-confirm"
              ></button>
            </ng-template>
          </p-dialog>
        }


        @if (displayRequestorRepresentativeNameDialog) {
          <div>
            <p-dialog header="Member Representative Name"
              [(visible)]="displayRequestorRepresentativeNameDialog"
              [modal]="true"
              appendTo="body"
              [style]="{ width: '400px' }"
              [minY]="70"
              [closable]="false"
              [maximizable]="false">
              <form [formGroup]="authorizationForm">
                <div class="p-fluid p-form-widget mt-1">
                  <div class="field">
                    <label>Member Name:</label>
                    <div class="p-field-value">
                      <input pInputText
                        trimInputValue
                        formControlName="memberRepresentative"
                        maxlength="25" />
                        @if (memberRepresentative.hasError('required')) {
                          <small class="p-error">
                            Enter Member Representative Name Only.
                            Do NOT include any other information besides first and last name
                          </small>
                        }
                        @if (memberRepresentative.hasError('maxlength')) {
                          <small class="p-error">
                            Must be up to 25 characters long.
                          </small>
                        }
                      </div>
                    </div>
                  </div>
                </form>
                <ng-template #footer>
                  <button type="button"
                    pButton
                    icon="pi pi-times"
                    (click)="displayRequestorRepresentativeNameDialog=false"
                    label="Save"
                  [disabled]="!memberRepresentative.valid"></button>
                </ng-template>
              </p-dialog>
            </div>
          }
